/**
 * The LEVEL EDITOR page (dev/admin tool, gated by {@link isEditorEnabled}). It owns the working
 * {@link LevelDocument} and the selection; the {@link EditorStage} is a controlled Pixi view that
 * renders the doc WYSIWYG and reports pointer gestures back as pure {@link editor-doc} mutations. The
 * right rail is the chrome (palette / inspector / validation), the top bar is the toolbar. Everything
 * autosaves to localStorage; the durable path is JSON export/import.
 *
 * A DEV-only `window.__duckEditor` driver mirrors the office's `__duck*` hooks so the e2e can author +
 * assert a level without pixel-clicking (it calls the SAME mutation functions the UI does).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  blankLevel,
  levelHasErrors,
  officeLevel,
  parseLevel,
  serializeLevel,
  validateLevel,
  type InteractiveObject,
  type LevelDocument,
  type SeatKind,
  type WorldDoor,
  type WorldThemeId,
} from '@duck/contracts';
import { physicalKey } from '../../net/keyboard';
import { usePageMeta } from '../../lib/page-meta';
import { EditorStage } from './EditorStage';
import { HotkeyLegend, Inspector, Palette, Toolbar, ValidationPanel } from './EditorPanels';
import { captureOfficeDecor } from './capture-decor';
import {
  addDecor,
  addDoor,
  addObject,
  addSeat,
  addZone,
  clearWorkingDoc,
  deleteSelected,
  duplicateSelected,
  findItem,
  loadWorkingDoc,
  moveSelected,
  rotateSelected,
  saveWorkingDoc,
  updateSelected,
  type PlacementArm,
  type SelectionRef,
} from './editor-doc';

/** One rotation step for the R hotkey + the inspector rotate buttons (15°). */
const ROTATE_STEP = Math.PI / 12;

export function EditorPage(): JSX.Element {
  usePageMeta({ title: 'Map editor — Duck Duck Space', noindex: true });
  const navigate = useNavigate();

  const [doc, setDoc] = useState<LevelDocument>(() => loadWorkingDoc());
  const [sel, setSel] = useState<SelectionRef | null>(null);
  const [placement, setPlacement] = useState<PlacementArm | null>(null);
  const [gridOn, setGridOn] = useState(true);
  const [baked, setBakedState] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const issues = useMemo(() => validateLevel(doc), [doc]);
  const hasErrors = useMemo(() => levelHasErrors(issues), [issues]);

  // Refs so the imperative stage callbacks + the dev hook always read the LATEST state.
  const docRef = useRef(doc);
  docRef.current = doc;
  const selRef = useRef(sel);
  selRef.current = sel;
  const stageRef = useRef<EditorStage | null>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  const flash = useCallback((msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice((cur) => (cur === msg ? null : cur)), 2600);
  }, []);

  // ── Undo/redo history. Every settled mutation goes through `commit`, which snapshots the prior doc so
  // Ctrl/Cmd+Z walks back. Drags settle once (on pointer-up) → one history entry per gesture, not per frame.
  const history = useRef<{ past: LevelDocument[]; future: LevelDocument[] }>({ past: [], future: [] });
  const HISTORY_CAP = 200;
  const [histLen, setHistLen] = useState({ past: 0, future: 0 });
  const syncHistLen = useCallback(() => {
    setHistLen({ past: history.current.past.length, future: history.current.future.length });
  }, []);

  const commit = useCallback(
    (next: LevelDocument, nextSel?: SelectionRef | null) => {
      history.current.past.push(docRef.current);
      if (history.current.past.length > HISTORY_CAP) history.current.past.shift();
      history.current.future = [];
      setDoc(next);
      if (nextSel !== undefined) setSel(nextSel);
      syncHistLen();
    },
    [syncHistLen],
  );

  const undo = useCallback(() => {
    const h = history.current;
    if (!h.past.length) return;
    h.future.push(docRef.current);
    setDoc(h.past.pop()!);
    setSel(null);
    syncHistLen();
  }, [syncHistLen]);

  const redo = useCallback(() => {
    const h = history.current;
    if (!h.future.length) return;
    h.past.push(docRef.current);
    setDoc(h.future.pop()!);
    setSel(null);
    syncHistLen();
  }, [syncHistLen]);

  // Place a newly-armed palette item at a clicked world point (the page owns id minting + selection).
  const handlePlace = useCallback((arm: PlacementArm, x: number, y: number) => {
    const d = docRef.current;
    const res =
      arm.type === 'object'
        ? addObject(d, arm.kind as InteractiveObject['kind'], x, y)
        : arm.type === 'seat'
          ? addSeat(d, arm.kind as SeatKind, x, y)
          : arm.type === 'decor'
            ? addDecor(d, arm.kind ?? '', x, y)
            : addZone(d, x, y);
    commit(res.doc, { type: arm.type, id: res.id });
    setPlacement(null); // single place, then back to the select tool (calm, predictable)
  }, [commit]);

  // Rotate the selection by `delta` radians (R / Shift+R hotkey + the inspector ↺ ↻ buttons).
  const handleRotate = useCallback(
    (delta: number) => {
      const s = selRef.current;
      if (s) commit(rotateSelected(docRef.current, s, delta));
    },
    [commit],
  );

  // Duplicate the selection (Ctrl/Cmd+D) — a staple of map editors for repeating furniture.
  const duplicate = useCallback(() => {
    const s = selRef.current;
    if (!s) return;
    const res = duplicateSelected(docRef.current, s, 30, 30);
    if (res) commit(res.doc, { type: s.type, id: res.id });
  }, [commit]);

  // Nudge the selection by the arrow keys (Shift = a bigger step). Doors slide along their edge.
  const nudge = useCallback(
    (dir: string, big: boolean) => {
      const s = selRef.current;
      if (!s) return;
      const item = findItem(docRef.current, s) as { x?: number; y?: number; pos?: number } | null;
      if (!item) return;
      const step = big ? 20 : 4;
      const dx = dir === 'arrowleft' ? -step : dir === 'arrowright' ? step : 0;
      const dy = dir === 'arrowup' ? -step : dir === 'arrowdown' ? step : 0;
      if (s.type === 'door') {
        commit(updateSelected(docRef.current, s, { pos: (item.pos ?? 0) + (dx || dy) }));
        return;
      }
      commit(moveSelected(docRef.current, s, (item.x ?? 0) + dx, (item.y ?? 0) + dy));
    },
    [commit],
  );

  const handleToggleBake = useCallback(() => {
    const next = !(stageRef.current?.isBaked() ?? false);
    const count = stageRef.current?.setBaked(next) ?? 0;
    setBakedState(next);
    if (next) {
      setPlacement(null);
      flash(`⚡ Baked ${count} elements → 1 sprite (1 draw call)`);
    }
  }, [flash]);

  // ── Stage lifecycle (mount once) ──
  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const stage = new EditorStage(docRef.current, {
      onSelect: (s) => setSel(s),
      onMutate: (next) => commit(next), // settled drag → one undo entry
    });
    stage.onPlace = handlePlace;
    stageRef.current = stage;
    void stage.init(el);
    return () => {
      stage.destroy();
      stageRef.current = null;
    };
  }, [handlePlace, commit]);

  // ── Keep the stage + persistence in sync with state ──
  useEffect(() => {
    saveWorkingDoc(doc);
    stageRef.current?.setDoc(doc);
  }, [doc]);
  useEffect(() => stageRef.current?.setSelection(sel), [sel]);
  useEffect(() => stageRef.current?.setGrid(gridOn), [gridOn]);
  useEffect(() => stageRef.current?.setPlacement(placement), [placement]);

  // ── Toolbar / inspector / validation actions ──
  // Start a brand-new BLANK map (empty canvas), vs resetting to the office. Both are undoable.
  const handleNewBlank = useCallback(() => {
    commit(blankLevel(), null);
    setPlacement(null);
    flash('New blank map — place a room from the palette to begin');
  }, [commit, flash]);

  const handleNewOffice = useCallback(() => {
    clearWorkingDoc();
    commit(officeLevel(), null);
    setPlacement(null);
    flash('Loaded the office layout');
  }, [commit, flash]);

  const handleThemeChange = useCallback((id: WorldThemeId) => commit({ ...docRef.current, themeId: id }), [commit]);

  const handleExport = useCallback(() => {
    const json = serializeLevel(docRef.current);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${docRef.current.id || 'level'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    void navigator.clipboard?.writeText(json).catch(() => undefined);
    flash('Exported — downloaded + copied to clipboard');
  }, [flash]);

  const handleImport = useCallback(
    (json: string) => {
      try {
        commit(parseLevel(json), null);
        flash('Imported level');
      } catch {
        flash("Couldn't read that file — not a valid level JSON");
      }
    },
    [commit, flash],
  );

  const handleUpdate = useCallback(
    (patch: Record<string, unknown>) => {
      const s = selRef.current;
      if (s) commit(updateSelected(docRef.current, s, patch));
    },
    [commit],
  );

  const handleAddDoor = useCallback(
    (side: WorldDoor['side']) => {
      const s = selRef.current;
      if (s?.type !== 'zone') return;
      const res = addDoor(docRef.current, s.id, side);
      if (res) commit(res.doc, { type: 'door', id: res.id });
    },
    [commit],
  );

  const handleDelete = useCallback(() => {
    const s = selRef.current;
    if (!s) return;
    commit(deleteSelected(docRef.current, s), null);
  }, [commit]);

  const selectById = useCallback((id: string) => {
    const d = docRef.current;
    if (d.zones.some((z) => z.id === id)) setSel({ type: 'zone', id });
    else if (d.doors.some((x) => x.id === id)) setSel({ type: 'door', id });
    else if (d.objects.some((o) => o.id === id)) setSel({ type: 'object', id });
    else if (d.seats.some((s) => s.id === id)) setSel({ type: 'seat', id });
    else if (d.decor.some((x) => x.id === id)) setSel({ type: 'decor', id });
  }, []);

  // ── KEYBOARD SHORTCUTS (map-editor staples; layout-independent via physicalKey so RU/AZERTY work). ──
  // Esc deselect · Del/Backspace delete · Ctrl/Cmd+Z/Y undo·redo · Ctrl/Cmd+D duplicate · Ctrl/Cmd+S export
  // · V select tool · G grid · F fit · B bake · Arrows nudge (Shift = bigger step).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
        return; // never hijack typing in a field
      const k = physicalKey(e);
      if (e.ctrlKey || e.metaKey) {
        if (k === 'z') {
          e.preventDefault();
          if (e.shiftKey) redo();
          else undo();
        } else if (k === 'y') {
          e.preventDefault();
          redo();
        } else if (k === 'd') {
          e.preventDefault();
          duplicate();
        } else if (k === 's') {
          e.preventDefault();
          handleExport();
        }
        return;
      }
      switch (k) {
        case 'escape':
          setPlacement(null);
          setSel(null);
          break;
        case 'delete':
        case 'backspace':
          e.preventDefault();
          handleDelete();
          break;
        case 'v':
          setPlacement(null);
          break;
        case 'r':
          e.preventDefault();
          handleRotate(e.shiftKey ? -ROTATE_STEP : ROTATE_STEP);
          break;
        case 'g':
          setGridOn((g) => !g);
          break;
        case 'f':
          stageRef.current?.fit();
          break;
        case 'b':
          handleToggleBake();
          break;
        case 'arrowup':
        case 'arrowdown':
        case 'arrowleft':
        case 'arrowright':
          e.preventDefault();
          nudge(k, e.shiftKey);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, duplicate, handleExport, handleDelete, handleToggleBake, nudge, handleRotate]);

  // ── DEV-only e2e driver (mirrors the office __duck* hooks) ──
  useEffect(() => {
    if (!import.meta.env?.DEV || typeof window === 'undefined') return;
    type Driver = Record<string, unknown>;
    (window as unknown as { __duckEditor?: () => Driver }).__duckEditor = () => ({
      doc: structuredClone(docRef.current),
      issues: validateLevel(docRef.current),
      hasErrors: levelHasErrors(validateLevel(docRef.current)),
      selection: selRef.current,
      place: (type: PlacementArm['type'], kind: string | undefined, x: number, y: number) => {
        handlePlace({ type, kind }, x, y);
        return selRef.current?.id ?? null;
      },
      move: (type: SelectionRef['type'], id: string, x: number, y: number) =>
        commit(moveSelected(docRef.current, { type, id }, x, y)),
      update: (type: SelectionRef['type'], id: string, patch: Record<string, unknown>) =>
        commit(updateSelected(docRef.current, { type, id }, patch)),
      select: (type: SelectionRef['type'] | null, id: string) =>
        setSel(type ? { type, id } : null),
      del: (type: SelectionRef['type'], id: string) =>
        commit(deleteSelected(docRef.current, { type, id }), null),
      rotate: (type: SelectionRef['type'], id: string, delta: number) =>
        commit(rotateSelected(docRef.current, { type, id }, delta)),
      addDoor: (zoneId: string, side: WorldDoor['side']) => {
        const r = addDoor(docRef.current, zoneId, side);
        if (r) commit(r.doc, { type: 'door', id: r.id });
        return r?.id ?? null;
      },
      exportJSON: () => serializeLevel(docRef.current),
      importJSON: (json: string) => {
        try {
          commit(parseLevel(json), null);
          return { ok: true };
        } catch (e) {
          return { ok: false, error: String(e) };
        }
      },
      reset: () => commit(officeLevel(), null),
      newBlank: () => commit(blankLevel(), null),
      undo: () => undo(),
      redo: () => redo(),
      duplicate: () => duplicate(),
      toggleBake: () => handleToggleBake(),
    });
    // One-time DECOR CAPTURE driver: dumps the office's tagged furniture as DecorItem[] (used to generate
    // the committed OFFICE_DECOR). Returns plain data so an e2e / a manual dump can read it.
    (window as unknown as { __captureOfficeDecor?: () => unknown }).__captureOfficeDecor = () =>
      captureOfficeDecor();
    return () => {
      delete (window as unknown as { __duckEditor?: unknown }).__duckEditor;
      delete (window as unknown as { __captureOfficeDecor?: unknown }).__captureOfficeDecor;
    };
  }, [handlePlace, handleToggleBake, commit, undo, redo, duplicate]);

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#0b1020' }}>
      <div style={{ padding: 8, display: 'flex', gap: 8 }}>
        <button
          onClick={() => navigate('/lobby')}
          style={{
            background: 'rgba(40,52,86,0.9)',
            border: '1px solid rgba(120,140,190,0.3)',
            borderRadius: 8,
            color: '#eef1ff',
            padding: '6px 10px',
            cursor: 'pointer',
            font: '13px ui-sans-serif, system-ui, sans-serif',
            flexShrink: 0,
          }}
          title="Back to the office"
        >
          ↩ Office
        </button>
        <div style={{ flex: 1 }}>
          <Toolbar
            doc={doc}
            gridOn={gridOn}
            baked={baked}
            hasErrors={hasErrors}
            issueCount={issues.length}
            canUndo={histLen.past > 0}
            canRedo={histLen.future > 0}
            onNewBlank={handleNewBlank}
            onNewOffice={handleNewOffice}
            onUndo={undo}
            onRedo={redo}
            onFit={() => stageRef.current?.fit()}
            onToggleGrid={() => setGridOn((g) => !g)}
            onToggleBake={handleToggleBake}
            onThemeChange={handleThemeChange}
            onExport={handleExport}
            onImport={handleImport}
          />
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div ref={mountRef} style={{ flex: 1, position: 'relative', minWidth: 0, overflow: 'hidden' }} />
        <div
          style={{
            width: 312,
            flexShrink: 0,
            overflowY: 'auto',
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderLeft: '1px solid rgba(120,140,190,0.2)',
          }}
        >
          <Palette placement={placement} onArm={setPlacement} />
          <Inspector
            doc={doc}
            sel={sel}
            onUpdate={handleUpdate}
            onAddDoor={handleAddDoor}
            onRotate={handleRotate}
            onDelete={handleDelete}
          />
          <ValidationPanel issues={issues} onSelectIssue={selectById} />
          <HotkeyLegend />
        </div>
      </div>

      {notice && (
        <div
          style={{
            position: 'fixed',
            bottom: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(13,18,34,0.95)',
            border: '1px solid rgba(120,140,190,0.3)',
            borderRadius: 10,
            color: '#eef1ff',
            padding: '8px 14px',
            font: '13px ui-sans-serif, system-ui, sans-serif',
            zIndex: 10,
          }}
          role="status"
        >
          {notice}
        </div>
      )}
    </div>
  );
}
